import {
  accountColumnLabel,
  accountColumnLabelLines,
} from '@components/AccountAllocationGrid/accountColumnLabel';
import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import { Alert, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type {
  ReviewBlockCode,
  ReviewDecision,
} from '../editAccountAllocations/reviewAccountAllocationsModel';

// D2: Back only helps when re-staging fixes it. A moved baseline is kept per
// report, so re-staging keeps the old one and blocks again: those restart.
const BLOCKED_COPY: Record<ReviewBlockCode, string> = {
  MISSING_STAGING: 'reviewAccountAllocations.blocked.MISSING_STAGING',
  STALE_REPORT: 'reviewAccountAllocations.blocked.STALE_REPORT',
  MEMBERSHIP_CHANGED: 'reviewAccountAllocations.blocked.MEMBERSHIP_CHANGED',
  BASELINE_CHANGED: 'reviewAccountAllocations.blocked.BASELINE_CHANGED',
  INVALID_ACCOUNT_TOTALS:
    'reviewAccountAllocations.blocked.INVALID_ACCOUNT_TOTALS',
};

export type ReviewAccountAllocationsBlockedProp = {
  decision: Extract<ReviewDecision, { status: 'BLOCKED' }>;
  report: AccountAllocationGridReport;
  canCancel: boolean;
  testId: string;
};

export const ReviewAccountAllocationsBlocked = ({
  decision,
  report,
  canCancel,
  testId,
}: ReviewAccountAllocationsBlockedProp) => {
  const { t } = useTranslation('workflowCompletions');

  // An account that left the household is no longer in the report to name.
  const accounts = [...(decision.added ?? []), ...(decision.removed ?? [])]
    .flatMap((subAccountId) => {
      const account = report.subAccounts.find(
        (candidate) => candidate.subAccount.id === subAccountId,
      )?.subAccount.account;
      return account
        ? [accountColumnLabelLines(accountColumnLabel(account, t)).join(' ')]
        : [];
    })
    .join(', ');

  return (
    <Alert severity='warning'>
      <Typography
        variant='bodyMedium'
        sx={{ fontWeight: 500 }}
        data-testid={`${testId}-blocked`}
      >
        {t(BLOCKED_COPY[decision.code], {
          context: accounts ? undefined : 'unnamed',
          accounts,
          restart: t(
            canCancel
              ? 'reviewAccountAllocations.restart.cancel'
              : 'reviewAccountAllocations.restart.close',
          ),
        })}
      </Typography>
    </Alert>
  );
};
